import { useEffect, useState } from 'react';
import type { BrandContentResponseModel, PreferenceDto } from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { Icon } from '@/components/ui/icon';
import { ShopBrandsAll } from './all';

export type ShopBrandsProps = {
  brands: BrandContentResponseModel[];
  userPreferences: PreferenceDto[];
  hasActiveMembership?: boolean;
  onBookmarkToggle: (brand: BrandContentResponseModel, isSelected: boolean) => void;
  onBrandClick: (brand: BrandContentResponseModel) => void;
  onBack?: () => void;
};

function BrandRow({
  brand,
  isSelected,
  isDisabled,
  onBookmarkToggle,
  onBrandClick,
}: {
  brand: BrandContentResponseModel;
  isSelected: boolean;
  isDisabled: boolean;
  onBookmarkToggle: (brand: BrandContentResponseModel, isSelected: boolean) => void;
  onBrandClick: (brand: BrandContentResponseModel) => void;
}) {
  const t = useClientTranslation();

  if (isDisabled) {
    return (
      <div className="border-jncquoi-neutral-200 flex items-center gap-4 border-b py-6">
        <Icon name="bookmark" width={16} height={16} className="text-jncquoi-neutral-700 shrink-0" />
        <span className="font-pp-neue-montreal text-jncquoi-neutral-700 min-w-0 flex-1 text-[12px] tracking-[0.5px] uppercase">
          {brand.name}
        </span>
        <span className="font-pp-neue-montreal text-jncquoi-neutral-700 shrink-0 text-[10px] font-semibold tracking-[0.5px] whitespace-nowrap uppercase">
          {t('App.Labels.MembersOnly')}
        </span>
      </div>
    );
  }

  return (
    <div className="border-jncquoi-neutral-200 flex items-center gap-4 border-b py-6">
      <button
        type="button"
        onClick={() => onBookmarkToggle(brand, isSelected)}
        className="flex shrink-0 items-center justify-center"
      >
        <Icon
          name={isSelected ? 'bookmark-solid' : 'bookmark'}
          width={16}
          height={16}
          className="text-jncquoi-neutral-1000"
        />
      </button>
      <button
        type="button"
        onClick={() => onBrandClick(brand)}
        className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 text-left text-[12px] tracking-[0.5px] uppercase"
      >
        {brand.name}
      </button>
    </div>
  );
}

function BrandSection({
  label,
  brands,
  userPreferences,
  hasActiveMembership,
  onBookmarkToggle,
  onBrandClick,
}: {
  label: string;
  brands: BrandContentResponseModel[];
  userPreferences: PreferenceDto[];
  hasActiveMembership?: boolean;
  onBookmarkToggle: (brand: BrandContentResponseModel, isSelected: boolean) => void;
  onBrandClick: (brand: BrandContentResponseModel) => void;
}) {
  if (!brands.length) return null;
  return (
    <div className="flex flex-col">
      <div className="border-jncquoi-neutral-200 border-b py-6">
        <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
          {label}
        </span>
      </div>
      {brands.map(brand => {
        const membersOnly = brand.properties?.membersOnly ?? false;
        const isDisabled = membersOnly && !hasActiveMembership;
        return (
          <BrandRow
            key={brand.id}
            brand={brand}
            isSelected={userPreferences.some(p => p.subcategoryNodeKey === brand.id)}
            isDisabled={isDisabled}
            onBookmarkToggle={onBookmarkToggle}
            onBrandClick={onBrandClick}
          />
        );
      })}
    </div>
  );
}

export function ShopBrands({
  brands,
  userPreferences,
  hasActiveMembership,
  onBookmarkToggle,
  onBrandClick,
  onBack,
}: ShopBrandsProps) {
  const t = useClientTranslation();
  const setScreenHeadOverride = useUIStore(state => state.setScreenHeadOverride);
  const [showAll, setShowAll] = useState(false);

  const ourBrands = brands.filter(b => b.properties?.isOurBrand);
  const topBrands = brands.filter(b => b.properties?.isTopBrand);

  useEffect(() => {
    if (showAll) {
      setScreenHeadOverride({
        title: t('App.Labels.AllBrands'),
        onBack: () => {
          setShowAll(false);
          onBack?.();
        },
      });
    }
  }, [showAll, setScreenHeadOverride, t, onBack]);

  if (showAll) {
    return (
      <ShopBrandsAll
        brands={brands}
        userPreferences={userPreferences}
        hasActiveMembership={hasActiveMembership}
        onBookmarkToggle={onBookmarkToggle}
        onBrandClick={onBrandClick}
      />
    );
  }

  return (
    <div className="flex flex-col gap-8 px-4 pb-0">
      <button
        type="button"
        onClick={() => setShowAll(true)}
        className="border-jncquoi-neutral-900! flex w-full items-center justify-between border-b-[0.5px] py-2.5 pr-0"
      >
        <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
          {t('App.Labels.AllBrands')}
        </span>
        <div className="flex size-11 items-center justify-center">
          <Icon name="nav-arrow-right" width={20} height={20} className="text-jncquoi-neutral-1000" />
        </div>
      </button>
      <BrandSection
        label={t('App.Labels.OurBrands')}
        brands={ourBrands}
        userPreferences={userPreferences}
        hasActiveMembership={hasActiveMembership}
        onBookmarkToggle={onBookmarkToggle}
        onBrandClick={onBrandClick}
      />
      <BrandSection
        label={t('App.Labels.TopBrands')}
        brands={topBrands}
        userPreferences={userPreferences}
        hasActiveMembership={hasActiveMembership}
        onBookmarkToggle={onBookmarkToggle}
        onBrandClick={onBrandClick}
      />
    </div>
  );
}
